import type { FC, ReactNode } from 'react'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

interface ScaledSheetProps {
    children: ReactNode
    className?: string
    maxScale?: number
}

/**
 * Shrinks the A4 sheet to fit narrow screens without changing its real size,
 * so print and PDF output always use the full A4 layout.
 */
export const ScaledSheet: FC<ScaledSheetProps> = ({ children, className, maxScale = 1 }): ReactNode => {
    const frameRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const [scale, setScale] = useState(1)
    const [height, setHeight] = useState<number | null>(null)

    useBrowserLayoutEffect(() => {
        const frame = frameRef.current
        const content = contentRef.current

        if (!frame || !content) return

        const update = (): void => {
            const naturalWidth = content.offsetWidth || 794
            const next = Math.min(maxScale, frame.clientWidth / naturalWidth)

            setScale(next)
            setHeight(content.offsetHeight * next)
        }

        update()

        const observer = new ResizeObserver(update)

        observer.observe(frame)
        observer.observe(content)

        return () => observer.disconnect()
    }, [maxScale])

    return (
        <div
            ref={frameRef}
            className={cn('rx-scale-frame relative w-full overflow-hidden', className)}
            style={{ height: height ?? undefined }}
        >
            <div
                ref={contentRef}
                className='rx-scale-content absolute top-0 left-1/2 w-max origin-top'
                style={{ transform: `translateX(-50%) scale(${scale})` }}
            >
                {children}
            </div>
        </div>
    )
}
